/**
 * Checkbox Component
 * Accessible checkbox input with label
 */

import React from 'react';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Label text for checkbox */
  label?: string;
  /** Helper text below checkbox */
  helperText?: string;
  /** Error message */
  error?: string;
}

/**
 * Checkbox Component
 *
 * @example
 * <Checkbox
 *   label="I agree to the terms and conditions"
 * />
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      helperText,
      error,
      className = '',
      id,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;

    // Build checkbox classes
    const checkboxClasses = `${styles.input} ${className}`;

    // Build container classes
    const containerClasses = `${styles.container} ${disabled ? styles.disabled : ''}`;

    // Build label classes
    const labelClasses = `${disabled ? styles.disabled : ''}`;

    return (
      <div>
        <div className={containerClasses}>
          <input
            ref={ref}
            id={checkboxId}
            type="checkbox"
            disabled={disabled}
            className={checkboxClasses}
            {...props}
          />

          {label && (
            <label htmlFor={checkboxId} className={labelClasses}>
              {label}
            </label>
          )}
        </div>

        {error && <p className={styles.errorText}>{error}</p>}

        {helperText && !error && <p className={styles.helperText}>{helperText}</p>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
